/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useUserSubmit.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for handling user form submission.
────────────────────────────────────────────────────────────────*/

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useAuthStore } from '../../../store/auth';
import { createUser, updateUser } from '../api/usersApi';
import { UserFormData } from './useUserForm';
import { validateUserForm } from '../utils/validators';
import { User } from '../types/users.types';

export function useUserSubmit(
  mode: 'create' | 'edit',
  id: string | undefined,
  formData: UserFormData,
  originalData: UserFormData,
  setOriginalData: (data: UserFormData) => void,
  uploadAvatarAfterCreate?: (userId: string) => Promise<void>,
  setActiveTab?: (tab: string) => void,
) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError, showInfo } = useNotifications();
  const { user: currentUser } = useAuthStore();
  const tenantSlug = currentUser?.tenant?.slug || t('users.defaultTenant');

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const hasChanges =
    mode === 'create' ||
    JSON.stringify(formData) !== JSON.stringify(originalData);

  const handleSubmit = async (e?: React.FormEvent): Promise<User | undefined> => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Always validate the form before submitting
    const validationErrors = validateUserForm(formData, mode, t);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors as Record<string, string>);

      // Navigate to the correct tab if there's an error
      if (setActiveTab) {
        const appsKeys = ['profileId', 'logProfileId', 'switchboardRole', 'switchboardLogProfileId'];
        if (Object.keys(validationErrors).some(k => appsKeys.includes(k))) {
          setActiveTab('apps');
        } else {
          setActiveTab('general');
        }
      }

      const errorMsgs = Object.values(validationErrors).filter(msg => typeof msg === 'string').join(' • ');
      showError(`${t('users.errors.fixForm')}${errorMsgs ? ': ' + errorMsgs : ''}`);
      return undefined;
    }

    // Check for changes after validation
      // hasChanges validation removed

    try {
      setSaving(true);
      setErrors({});

      // Prepare data for submission - clean and validate data
      const submitData: UserFormData & Record<string, unknown> = { ...formData };

      // In create mode, validate password before sending
      if (mode === 'create') {
        // Check if password exists and is not empty
        if (!submitData.password || typeof submitData.password !== 'string' || !submitData.password.trim()) {
          const passReqError = t('users.validation.passwordRequired');
          setErrors({ password: passReqError });
          showError(`${t('users.errors.fixForm')}: ${passReqError}`);
          setSaving(false);
          return undefined;
        }

        // Check password length
        const trimmedPassword = submitData.password.trim();
        if (trimmedPassword.length < 8) {
          const passLenError = t('users.validation.passwordMinLength');
          setErrors({ password: passLenError });
          showError(`${t('users.errors.fixForm')}: ${passLenError}`);
          setSaving(false);
          return undefined;
        }

        // Ensure we send the trimmed password
        submitData.password = trimmedPassword;
      } else if (mode === 'edit' && (!submitData.password || !submitData.password.trim())) {
        // In edit mode, remove empty password
        delete submitData.password;
      }

      // Clean optional fields - remove empty strings and convert to proper types
      if (submitData.profileId === '' || submitData.profileId === null || submitData.profileId === undefined) {
        submitData.profileId = null;
      } else if (submitData.profileId) {
        // Convert to number if it's a numeric string, otherwise keep as string
        const profileIdNum = Number(submitData.profileId);
        submitData.profileId = (!isNaN(profileIdNum) && isFinite(profileIdNum) ? profileIdNum : submitData.profileId) as string;
      }

      if (submitData.logProfileId === '' || submitData.logProfileId === null || submitData.logProfileId === undefined) {
        submitData.logProfileId = null;
      } else if (submitData.logProfileId) {
        // Convert to number if it's a numeric string, otherwise keep as string
        const logProfileIdNum = Number(submitData.logProfileId);
        submitData.logProfileId = (!isNaN(logProfileIdNum) && isFinite(logProfileIdNum) ? logProfileIdNum : submitData.logProfileId) as string;
      }

      if ((submitData.extensionId as unknown) === '' || submitData.extensionId === null || submitData.extensionId === undefined) {
        submitData.extensionId = null;
      } else if (submitData.extensionId) {
        const extIdNum = Number(submitData.extensionId);
        submitData.extensionId = (!isNaN(extIdNum) && isFinite(extIdNum) ? extIdNum : submitData.extensionId) as number;
      }

      // Ensure tenantIds is an array of valid values
      if (!Array.isArray(submitData.tenantIds)) {
        submitData.tenantIds = [];
      } else {
        // Filter out empty values and convert to proper types
        submitData.tenantIds = (submitData.tenantIds as unknown[])
          .filter((id: unknown) => id !== '' && id !== null && id !== undefined)
          .map((id: unknown) => {
            const numId = Number(id);
            return !isNaN(numId) && isFinite(numId) ? numId : id;
          }) as string[];
      }

      // Clean string fields - trim and remove if empty
      if (submitData.fullName !== undefined && submitData.fullName !== null && typeof submitData.fullName === 'string') {
        const trimmed = submitData.fullName.trim();
        submitData.fullName = trimmed || undefined;
      }
      if (submitData.department !== undefined && submitData.department !== null && typeof submitData.department === 'string') {
        const trimmed = submitData.department.trim();
        submitData.department = trimmed || undefined;
      }


      // Make API call
      let savedUser;
      try {
        savedUser =
          mode === 'edit' && id
            ? await updateUser(id, submitData, tenantSlug, currentUser?.id)
            : await createUser(submitData, tenantSlug, currentUser?.id);
      } catch (error) {
        throw error;
      }

      // Upload avatar if needed
      if (uploadAvatarAfterCreate && mode === 'create' && savedUser?.id) {
        await uploadAvatarAfterCreate(savedUser.id);
      }

      // Update originalData after successful save
      const newOriginalData: UserFormData = {
        ...formData,
        password: '', // Password is never in originalData
      };
      setOriginalData(newOriginalData);

      // Show success notification
      showSuccess(
        mode === 'create' ? t('users.userCreated') : t('users.userUpdated'),
      );

      return savedUser;
    } catch (error) {
      // Extract error message
      let errorMessage = error?.message ||
        (mode === 'create' ? t('users.errorCreating') : t('users.errorUpdating'));

      // Handle specific error cases
      if (errorMessage.includes('already exists') || errorMessage.includes('duplicate')) {
        const duplicateError = t('users.errors.userExists');
        // Check if it's username or email to show error in the right field
        if (errorMessage.toLowerCase().includes('username')) {
          setErrors({ username: duplicateError });
        } else if (errorMessage.toLowerCase().includes('email')) {
          setErrors({ email: duplicateError });
        }
        errorMessage = duplicateError;
      }

      showError(errorMessage);
      return undefined;
    } finally {
      setSaving(false);
    }
  };

  return {
    saving,
    errors,
    setErrors,
    hasChanges,
    handleSubmit,
  };
}

